
import React from 'react';
import { Award, Star, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import AnimateInView from '@/components/ui/animate-in-view';

export interface CaseStudyProps {
  id: string;
  title: string;
  image: string;
  challenge: string;
  solution: string;
  outcome: string;
  link: string;
}

interface CaseStudyCardProps {
  caseStudy: CaseStudyProps;
  index: number;
}

/**
 * @component HNWICaseStudyCard
 * @description Card component for showcasing premium case studies
 * @props {CaseStudyProps} caseStudy - Case study data to display
 * @props {number} index - Position in the list for animation sequencing
 * @returns {JSX.Element} Premium styled case study card
 */
const HNWICaseStudyCard: React.FC<CaseStudyCardProps> = ({ caseStudy, index }) => {
  // Select icon based on index for variety
  const icons = [<Award className="h-5 w-5" />, <Star className="h-5 w-5" />, <FileText className="h-5 w-5" />];
  const icon = icons[index % icons.length];
  
  return (
    <AnimateInView 
      animation="fade-in" 
      delay={index * 100}
      className="flex flex-col bg-white rounded-lg overflow-hidden shadow-premium hover:shadow-luxury-gold transition-all duration-500 border border-gold/10"
    >
      {/* Card Image */}
      <div className="relative h-60 overflow-hidden">
        <img 
          src={caseStudy.image} 
          alt={caseStudy.title} 
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          loading="lazy"
        />
        {/* Overlay with title */}
        <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/80 to-transparent flex items-end">
          <div className="p-6">
            <div className="flex items-center mb-2">
              <div className="mr-2 text-gold">
                {icon}
              </div>
              <span className="text-sm text-gold">Case Study</span>
            </div>
            <h3 className="text-xl font-serif text-white">{caseStudy.title}</h3>
          </div>
        </div>
      </div>
      
      {/* Card Content */}
      <div className="flex-grow p-6 space-y-4">
        {/* Challenge */}
        <div>
          <h4 className="text-forti-fi-dark font-medium mb-1">Challenge</h4>
          <p className="text-forti-fi-charcoal text-sm">{caseStudy.challenge}</p>
        </div>
        
        {/* Solution */}
        <div>
          <h4 className="text-forti-fi-dark font-medium mb-1">Solution</h4>
          <p className="text-forti-fi-charcoal text-sm">{caseStudy.solution}</p>
        </div>
        
        {/* Outcome */}
        <div>
          <h4 className="text-forti-fi-dark font-medium mb-1">Outcome</h4>
          <p className="text-forti-fi-charcoal text-sm">{caseStudy.outcome}</p>
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="p-6 pt-0">
        <Button asChild variant="outline" className="w-full justify-center">
          <Link to={caseStudy.link}>
            View Details
          </Link>
        </Button>
      </div>
    </AnimateInView>
  );
};

export default HNWICaseStudyCard;
